import { useMemo } from 'react';
import { Layer, Source, useMap as useMapboxMap } from 'react-map-gl/mapbox';

import {
  DropdownMini,
  Flex,
  Switch,
  Tooltip,
  TwoColumnTable,
} from '@hcs/design-system';
import {
  HcsDevToolItemTypeDefault,
  HcsDevToolItemTypeSwitch,
  HcsDevToolsPortal,
} from '@hcs/dev-tools';
import { HeatmapColorTheme, MapStyles, PropertyTypeEnum } from '@hcs/types';
import { formatNumber, NULL_VALUE, truncateToDecimalPlace } from '@hcs/utils';
import { ENV } from '@hcs/webapps';

import {
  isConsumerLayerEnum,
  latLngToTileCoords,
  PROPERTY_TYPE_TO_VECTILES_TYPE,
  tileCoordsToLatLng,
  useHcMap,
  useHcMapLayerLegendBreaks,
  useHcMapLayers,
} from '../../';

import styles from './HcMapDevTool.module.css';

const SIGNIFICANT_DIGITS = 6;

const TileBoundariesWithLabels = ({ mapId }: { mapId: string }) => {
  const { current: map } = useMapboxMap();
  const { mapState } = useHcMap(mapId);

  const { tileBoundariesGeoJSON, tileCentersGeoJSON } = useMemo<{
    tileBoundariesGeoJSON: GeoJSON.FeatureCollection | null;
    tileCentersGeoJSON: GeoJSON.FeatureCollection | null;
  }>(() => {
    if (!map || !mapState?.boundsInfo) {
      return { tileBoundariesGeoJSON: null, tileCentersGeoJSON: null };
    }

    const bounds = map.getBounds();
    const zoom = Math.floor(map.getZoom());

    const neViewport = bounds?.getNorthEast();
    const swViewport = bounds?.getSouthWest();
    if (!neViewport || !swViewport) {
      return { tileBoundariesGeoJSON: null, tileCentersGeoJSON: null };
    }
    const viewportTileNE = latLngToTileCoords({
      lat: neViewport.lat,
      lng: neViewport.lng,
      zoom,
    });
    const viewportTileSW = latLngToTileCoords({
      lat: swViewport.lat,
      lng: swViewport.lng,
      zoom,
    });

    // Calculate viewport bounds for label positioning
    const viewportNE = { lat: neViewport.lat, lng: neViewport.lng };
    const viewportSW = { lat: swViewport.lat, lng: swViewport.lng };

    const boundaryFeatures = [];
    const centerFeatures = [];

    for (let x = viewportTileSW.x; x <= viewportTileNE.x; x++) {
      for (let y = viewportTileNE.y; y <= viewportTileSW.y; y++) {
        const tileId = `${zoom}/${x}/${y}`;

        // Tile boundary
        const nw = tileCoordsToLatLng({ x, y, zoom });
        const ne = tileCoordsToLatLng({ x: x + 1, y, zoom });
        const se = tileCoordsToLatLng({ x: x + 1, y: y + 1, zoom });
        const sw = tileCoordsToLatLng({ x, y: y + 1, zoom });

        const polygonFeature: GeoJSON.Feature<GeoJSON.Polygon> = {
          type: 'Feature',
          properties: { tileId, tileX: x, tileY: y, zoom },
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [nw.lng, nw.lat],
                [ne.lng, ne.lat],
                [se.lng, se.lat],
                [sw.lng, sw.lat],
                [nw.lng, nw.lat],
              ],
            ],
          },
        };
        boundaryFeatures.push(polygonFeature);

        // Calculate label position with viewport constraint
        let labelLat = (nw.lat + se.lat) / 2;
        let labelLng = (nw.lng + se.lng) / 2;
        const textAnchor = 'center';
        const textOffset: [number, number] = [0, 0];

        // Adjust position if tile extends beyond viewport
        const tileVisibleBounds = {
          north: Math.min(nw.lat, viewportNE.lat),
          south: Math.max(se.lat, viewportSW.lat),
          east: Math.min(ne.lng, viewportNE.lng),
          west: Math.max(sw.lng, viewportSW.lng),
        };

        // Use the center of the visible portion of the tile
        labelLat = (tileVisibleBounds.north + tileVisibleBounds.south) / 2;
        labelLng = (tileVisibleBounds.east + tileVisibleBounds.west) / 2;

        const pointFeature: GeoJSON.Feature<GeoJSON.Point> = {
          type: 'Feature',
          properties: {
            tileId,
            tileX: x,
            tileY: y,
            zoom,
            textAnchor,
            textOffsetX: textOffset[0],
            textOffsetY: textOffset[1],
          },
          geometry: {
            type: 'Point',
            coordinates: [labelLng, labelLat],
          },
        };

        centerFeatures.push(pointFeature);
      }
    }

    const result: {
      tileBoundariesGeoJSON: GeoJSON.FeatureCollection | null;
      tileCentersGeoJSON: GeoJSON.FeatureCollection | null;
    } = {
      tileBoundariesGeoJSON: {
        type: 'FeatureCollection',
        features: boundaryFeatures,
      },
      tileCentersGeoJSON: {
        type: 'FeatureCollection',
        features: centerFeatures,
      },
    };
    return result;
  }, [map, mapState?.boundsInfo, mapState?.viewport.zoom]);

  if (!tileBoundariesGeoJSON || !tileCentersGeoJSON) return null;

  return (
    <>
      <Source id="tile-boundaries" type="geojson" data={tileBoundariesGeoJSON}>
        <Layer
          id="tile-boundaries-line"
          type="line"
          paint={{
            'line-color': '#ff0000',
            'line-width': 2,
            'line-opacity': 0.8,
          }}
        />
      </Source>

      <Source id="tile-centers" type="geojson" data={tileCentersGeoJSON}>
        <Layer
          id="tile-labels"
          type="symbol"
          layout={{
            'text-field': ['get', 'tileId'],
            'text-font': ['Open Sans Regular'],
            'text-size': 12,
            'text-anchor': 'center',
          }}
          paint={{
            'text-color': '#ff0000',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
          }}
        />
      </Source>
    </>
  );
};

const MapCoordinate = ({
  items,
}: {
  items: {
    mapValue: number | undefined;
    stateValue: number | undefined;
  }[];
}) => {
  return (
    <Flex>
      {items.map(({ mapValue, stateValue }) => {
        if (
          truncateToDecimalPlace(mapValue || 0, SIGNIFICANT_DIGITS) ===
          truncateToDecimalPlace(stateValue || 0, SIGNIFICANT_DIGITS)
        ) {
          return `${(mapValue || 0).toFixed(2)}`;
        }
        return (
          <Tooltip
            dataHcName=""
            trigger={
              <span
                className={styles.Mismatch}
              >{`${mapValue != null ? mapValue.toFixed(2) : NULL_VALUE}`}</span>
            }
            description={`State Value: ${stateValue} | Map Value: ${mapValue}`}
          />
        );
      })}
    </Flex>
  );
};

export interface MapDevToolValues {
  boundNE: { lat: number; lng: number } | undefined;
  boundSW: { lat: number; lng: number } | undefined;
  zoom: number | undefined;
  center: { lat: number; lng: number } | undefined;
  heatmapFeatures: GeoJSON.Feature[];
}

const useHcMapDevToolItems = (
  mapId: string,
  { boundNE, boundSW, zoom, center, heatmapFeatures }: MapDevToolValues,
) => {
  const {
    mapState,
    actions: {
      hcMapSetHeatmapTheme,
      hcMapChangeVectilesMetric,
      hcMapChangeMapStyle,
    },
  } = useHcMap(mapId);
  const { hcMapLayersState, vectilesLayerSource, vectilesMetricConfig } =
    useHcMapLayers(mapId);
  const intervals = useHcMapLayerLegendBreaks(mapId);
  if (!mapState) return [];
  const tileNE = mapState.boundsInfo?.northEast
    ? latLngToTileCoords({
        lat: mapState.boundsInfo.northEast.lat,
        lng: mapState.boundsInfo.northEast.lng,
        zoom: Math.floor(mapState.viewport.zoom),
      })
    : undefined;
  const tileSW = mapState.boundsInfo?.southWest
    ? latLngToTileCoords({
        lat: mapState.boundsInfo.southWest.lat,
        lng: mapState.boundsInfo.southWest.lng,
        zoom: Math.floor(mapState.viewport.zoom),
      })
    : undefined;
  const statsMax = intervals?.[0]?.max;
  const statsMin = intervals?.[0]?.min;
  const items: (
    | HcsDevToolItemTypeSwitch<boolean>
    | HcsDevToolItemTypeDefault
  )[] = [
    {
      type: 'item',
      item: {
        content: (
          <Flex justifyContent="space-between" fullWidth>
            <span>Heatmap Theme</span>
            <DropdownMini
              dataHcName=""
              value={mapState.heatmapTheme}
              options={[
                { label: 'Solutions', value: HeatmapColorTheme.Solutions },
                { label: 'ComeHome', value: HeatmapColorTheme.ComeHome },
              ]}
              onSelect={(v: HeatmapColorTheme) => {
                hcMapSetHeatmapTheme({ mapId, heatmapTheme: v });
              }}
            />
          </Flex>
        ),
      },
    },
    {
      type: 'item',
      item: {
        content: (
          <Flex justifyContent="space-between" fullWidth>
            <span>Map Style</span>
            <DropdownMini
              dataHcName=""
              value={mapState.mapStyle}
              options={[
                { label: 'Default', value: MapStyles.Default },
                { label: 'Satellite', value: MapStyles.Satellite },
                { label: 'Print', value: MapStyles.Print },
                { label: 'Light', value: MapStyles.Light },
              ]}
              onSelect={(v: MapStyles) => {
                hcMapChangeMapStyle({ mapId, mapStyle: v });
              }}
            />
          </Flex>
        ),
      },
    },
    {
      type: 'item',
      item: {
        largeContent: true,
        content: (
          <Flex direction="column" fullWidth alignItems="flex-start">
            <b>Viewport</b>
            <TwoColumnTable
              compact
              rows={[
                {
                  label: 'North East',
                  value: (
                    <Flex>
                      <MapCoordinate
                        items={[
                          {
                            mapValue: boundNE?.lat,
                            stateValue: mapState.boundsInfo?.northEast.lat,
                          },
                        ]}
                      />
                      <MapCoordinate
                        items={[
                          {
                            mapValue: boundNE?.lng,
                            stateValue: mapState.boundsInfo?.northEast.lng,
                          },
                        ]}
                      />
                    </Flex>
                  ),
                },
                {
                  label: 'South West',
                  value: (
                    <Flex>
                      <MapCoordinate
                        items={[
                          {
                            mapValue: boundSW?.lat,
                            stateValue: mapState.boundsInfo?.southWest.lat,
                          },
                        ]}
                      />
                      <MapCoordinate
                        items={[
                          {
                            mapValue: boundSW?.lng,
                            stateValue: mapState.boundsInfo?.southWest.lng,
                          },
                        ]}
                      />
                    </Flex>
                  ),
                },
                {
                  label: 'Center',
                  value: (
                    <Flex>
                      <MapCoordinate
                        items={[
                          {
                            mapValue: center?.lat,
                            stateValue: mapState.viewport.latitude,
                          },
                        ]}
                      />
                      <MapCoordinate
                        items={[
                          {
                            mapValue: center?.lng,
                            stateValue: mapState.viewport.longitude,
                          },
                        ]}
                      />
                    </Flex>
                  ),
                },
                {
                  label: 'Zoom',
                  value: (
                    <Flex>
                      <MapCoordinate
                        items={[
                          {
                            mapValue: zoom,
                            stateValue: mapState.viewport.zoom,
                          },
                        ]}
                      />
                    </Flex>
                  ),
                },
              ]}
            />
          </Flex>
        ),
      },
    },
  ];
  if (heatmapFeatures.length) {
    let maxValue: number | undefined = undefined;
    let minValue: number | undefined = undefined;
    let numAboveMax = 0;
    let numBelowMin = 0;
    let numNull = 0;
    const fullMetricString =
      vectilesMetricConfig?.vectilesMetric &&
      isConsumerLayerEnum(vectilesMetricConfig.vectilesMetric)
        ? vectilesMetricConfig.vectilesMetric
        : `${vectilesMetricConfig?.vectilesMetric}_${PROPERTY_TYPE_TO_VECTILES_TYPE[hcMapLayersState?.propertyType || PropertyTypeEnum.Sfr]?.toLowerCase()}`;
    heatmapFeatures?.forEach((feature) => {
      const value = feature.properties?.[fullMetricString];
      if (value == null) {
        numNull++;
        return;
      }
      if (statsMax !== undefined && value > statsMax) {
        numAboveMax++;
      }
      if (statsMin !== undefined && value < statsMin) {
        numBelowMin++;
      }
      if (maxValue === undefined || value > maxValue) {
        maxValue = value;
      }
      if (minValue === undefined || value < minValue) {
        minValue = value;
      }
    });
    const heatmapItems: (
      | HcsDevToolItemTypeDefault
      | HcsDevToolItemTypeDefault
    )[] = [
      {
        type: 'item',
        item: {
          content: (
            <Flex direction="column" fullWidth alignItems="flex-start">
              <Flex fullWidth justifyContent="space-between">
                <b>Vectile Layer</b>
                <Switch
                  dataHcName=""
                  value={!!hcMapLayersState?.vectilesMetricId}
                  switchOffOption={{ value: false }}
                  switchOnOption={{ value: true }}
                  onChange={(v) => {
                    if (!v) {
                      hcMapChangeVectilesMetric({
                        mapId,
                        vectilesMetricId: null,
                      });
                    }
                  }}
                />
              </Flex>
              <TwoColumnTable
                rows={[
                  {
                    label: 'Layer',
                    value: vectilesLayerSource?.vectilesLayer || NULL_VALUE,
                  },
                  {
                    label: 'Metric',
                    value: vectilesMetricConfig?.vectilesMetric || NULL_VALUE,
                  },
                  {
                    label: 'Min / Max Actual',
                    value:
                      minValue != null &&
                      maxValue != null &&
                      minValue < 10 &&
                      maxValue < 10
                        ? `${formatNumber(minValue, { precision: 2 })} / ${formatNumber(maxValue, { precision: 2 })}`
                        : `${formatNumber(minValue)} / ${formatNumber(maxValue)}`,
                  },
                  {
                    label: 'Min / Max Stats',
                    value:
                      statsMin != null &&
                      statsMax != null &&
                      statsMin < 10 &&
                      statsMax < 10
                        ? `${formatNumber(statsMin, { precision: 2 })} / ${formatNumber(statsMax, { precision: 2 })}`
                        : `${formatNumber(statsMin)} / ${formatNumber(statsMax)}`,
                  },
                  {
                    label: 'Outside Stats Range',
                    value: `${numBelowMin} below / ${numAboveMax} above`,
                  },
                  { label: 'Null Values', value: formatNumber(numNull) },
                  {
                    label: 'Visible Features',
                    value: formatNumber(heatmapFeatures.length),
                  },
                  {
                    label: 'Stat Tile Bounds',
                    value: `${tileSW?.x}-${tileNE?.x}/${tileNE?.y}-${tileSW?.y}`,
                  },
                ]}
                compact
              />
            </Flex>
          ),
        },
      },
    ];
    items.push(...heatmapItems);
  }
  return items;
};

const WithItems = ({
  mapId,
  map,
}: {
  mapId: string;
  map: MapDevToolValues;
}) => {
  return (
    <WithoutItems mapId={mapId} items={useHcMapDevToolItems(mapId, map)} />
  );
};

const WithoutItems = ({
  mapId,
  items,
}: {
  mapId: string;
  items?: (HcsDevToolItemTypeDefault | HcsDevToolItemTypeSwitch<boolean>)[];
}) => {
  const {
    mapState,
    actions: { hcMapDebugToggle },
  } = useHcMap(mapId);
  return (
    <HcsDevToolsPortal
      groups={[
        {
          dataHcName: 'hc-maps-dev-tool',
          title: `Map: ${mapId}`,
          expanded: !!items?.length,
          items: [
            {
              type: 'switch',
              item: {
                content: 'Enable Debugger',
              },
              switchProps: {
                dataHcName: 'map-devtool-debug',
                switchOffOption: {
                  value: false,
                  dataHcName: 'off',
                },
                switchOnOption: {
                  value: true,
                  dataHcName: 'on',
                },
                value: !!mapState?.debug,
                onChange: () => hcMapDebugToggle({ mapId }),
              },
            },
            ...(items || []),
          ],
        },
      ]}
    />
  );
};

export const HcMapDevToolInner = ({
  mapId,
  map,
}: {
  mapId: string;
  map: MapDevToolValues | undefined;
}) => {
  const { mapState } = useHcMap(mapId);
  if (map && mapState?.debug) {
    return <WithItems mapId={mapId} map={map} />;
  }
  return <WithoutItems mapId={mapId} />;
};

const HcMapDevToolEnabled = ({ mapId }: { mapId: string }) => {
  const { mapState } = useHcMap(mapId);
  const { vectilesLayerSource } = useHcMapLayers(mapId);
  const { current: map } = useMapboxMap();
  const bounds = map?.getBounds();
  const mapValues: MapDevToolValues = {
    boundNE: bounds?.getNorthEast(),
    boundSW: bounds?.getSouthWest(),
    zoom: map?.getZoom(),
    center: map?.getCenter(),
    heatmapFeatures:
      mapState?.heatmap.vectilesMetricId && mapState.debug
        ? map?.queryRenderedFeatures({
            layers: [`${vectilesLayerSource?.vectilesLayer}_layer`],
          }) || []
        : [],
  };
  return <HcMapDevToolInner mapId={mapId} map={mapValues} />;
};

export const HcMapDevTool = ({ mapId }: { mapId: string }) => {
  const { mapState } = useHcMap(mapId);
  if (!mapState || ENV === 'prod') return null;
  return (
    <>
      {mapState.debug ? (
        <HcMapDevToolEnabled mapId={mapId} />
      ) : (
        <HcMapDevToolInner mapId={mapId} map={undefined} />
      )}
      {mapState.debug && <TileBoundariesWithLabels mapId={mapId} />}
    </>
  );
};
